import { useState } from 'react';

import { Checkbox, Field, Input, Select } from '../../../components/ui';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import {
  TEN_CHE_DO_LIVENESS,
  TEN_DONG_TAC,
  TEN_KHI_THIEU,
  TEN_NGUON_OCR,
  TEN_TRUONG_GIAY_TO,
} from '../constants';
import { docSo, tongTrongSo } from '../nghiep-vu/cau-hinh';
import type { ChinhSach, DanhMucQuyTrinh, DongTac, KhiThieu } from '../types';

/** Con trỏ của mọi ô trên form — lỗi có con trỏ khác thì gom ra dải chung. */
export function coOChinhSach(truong: string): boolean {
  return truong.startsWith('#/chinhSach/');
}

/**
 * Ô số giữ **chữ người dùng đang gõ**, không giữ số: `0.` hay `0,7` là bước giữa của một số hợp lệ, và ép về số ở mỗi
 * phím sẽ nuốt mất dấu thập phân. Rỗng là `NaN` để máy chủ báo lỗi đúng ô, không lặng lẽ thành 0.
 */
function ONhapSo({
  nhan,
  goiY,
  giaTri,
  loi,
  suaDuoc,
  onDoi,
}: {
  nhan: string;
  goiY?: string;
  giaTri: number | null;
  loi?: string;
  suaDuoc: boolean;
  onDoi: (n: number) => void;
}) {
  const [chu, setChu] = useState(giaTri === null || !Number.isFinite(giaTri) ? '' : String(giaTri));
  return (
    <Field label={nhan} hint={goiY} error={loi}>
      {(p) => (
        <Input
          {...p}
          inputMode="decimal"
          value={chu}
          disabled={!suaDuoc}
          status={p['aria-invalid'] ? 'error' : undefined}
          onChange={(e) => {
            setChu(e.target.value);
            onDoi(docSo(e.target.value));
          }}
        />
      )}
    </Field>
  );
}

function Nhom({ tieuDe, moTa, children }: { tieuDe: string; moTa?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h3 className="text-sm font-semibold text-content-primary">{tieuDe}</h3>
        {moTa && <p className="mt-0.5 text-xs text-content-muted">{moTa}</p>}
      </div>
      {children}
    </section>
  );
}

/**
 * Chính sách của quy trình (`05` §7). Không kiểm gì ở đây: mọi lỗi và cảnh báo là của máy chủ, về qua xem trước
 * (`loi`, theo con trỏ). Hai bản luật — một ở giao diện, một ở máy chủ — sẽ trôi khỏi nhau.
 */
export function FormChinhSach({
  cs,
  luaChon,
  suaDuoc,
  loi,
  onDoi,
}: {
  cs: ChinhSach;
  luaChon: DanhMucQuyTrinh['luaChon'];
  suaDuoc: boolean;
  loi: Record<string, string>;
  onDoi: (cs: ChinhSach) => void;
}) {
  const { t, locale } = useT();
  const dat = <K extends keyof ChinhSach>(k: K, v: ChinhSach[K]) => onDoi({ ...cs, [k]: v });
  const tong = tongTrongSo(cs.weights);
  const doiTap = <T extends string>(ds: readonly T[], v: T, co: boolean): T[] =>
    co ? [...ds.filter((x) => x !== v), v] : ds.filter((x) => x !== v);

  return (
    <div className="space-y-3">
      <p className="text-xs text-content-muted">{t('ekyc.chinhSachQt.chuaTacDung')}</p>

      <Nhom tieuDe={t('ekyc.chinhSachQt.nguongTieuDe')} moTa={t('ekyc.chinhSachQt.nguongMo')}>
        <div className="grid items-start gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <ONhapSo
            nhan={t('ekyc.chinhSachQt.nguongDat')}
            goiY={t('ekyc.chinhSachQt.nguongDatGoiY')}
            giaTri={cs.thresholds.pass}
            loi={loi['#/chinhSach/thresholds/pass'] ?? loi['#/chinhSach/thresholds']}
            suaDuoc={suaDuoc}
            onDoi={(n) => dat('thresholds', { ...cs.thresholds, pass: n })}
          />
          <ONhapSo
            nhan={t('ekyc.chinhSachQt.nguongTuChoi')}
            goiY={t('ekyc.chinhSachQt.nguongTuChoiGoiY')}
            giaTri={cs.thresholds.reject}
            loi={loi['#/chinhSach/thresholds/reject']}
            suaDuoc={suaDuoc}
            onDoi={(n) => dat('thresholds', { ...cs.thresholds, reject: n })}
          />
          <ONhapSo
            nhan={t('ekyc.chinhSachQt.nguongMat')}
            goiY={t('ekyc.chinhSachQt.nguongMatGoiY')}
            giaTri={cs.faceMatchThreshold}
            loi={loi['#/chinhSach/faceMatchThreshold']}
            suaDuoc={suaDuoc}
            onDoi={(n) => dat('faceMatchThreshold', n)}
          />
        </div>
      </Nhom>

      <Nhom tieuDe={t('ekyc.chinhSachQt.trongSoTieuDe')} moTa={t('ekyc.chinhSachQt.trongSoMo')}>
        <div className="grid items-start gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {luaChon.yeuTo.map((k) => (
            <ONhapSo
              key={k}
              nhan={t(`ekyc.yeuTo.${k}`)}
              giaTri={cs.weights[k]}
              loi={loi[`#/chinhSach/weights/${k}`]}
              suaDuoc={suaDuoc}
              onDoi={(n) => dat('weights', { ...cs.weights, [k]: n })}
            />
          ))}
        </div>
        <p className={`text-sm ${loi['#/chinhSach/weights'] ? 'text-danger-700' : 'text-content-secondary'}`}>
          {t('ekyc.chinhSachQt.tongTrongSo', { tong: soVN(tong, locale) })}
          {loi['#/chinhSach/weights'] && <span role="alert"> — {loi['#/chinhSach/weights']}</span>}
        </p>
      </Nhom>

      <Nhom tieuDe={t('ekyc.chinhSachQt.livenessTieuDe')}>
        <div className="grid items-start gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <Field label={t('ekyc.chinhSachQt.cheDoLiveness')} error={loi['#/chinhSach/livenessMode']}>
            {(p) => (
              <Select
                className="w-full"
                id={p.id}
                aria-describedby={p['aria-describedby']}
                value={cs.livenessMode}
                disabled={!suaDuoc}
                onChange={(v) => dat('livenessMode', v)}
                options={luaChon.cheDoLiveness.map((v) => ({ value: v, label: t(TEN_CHE_DO_LIVENESS[v]) }))}
              />
            )}
          </Field>
          <ONhapSo
            nhan={t('ekyc.chinhSachQt.soDongTac')}
            giaTri={cs.challengeCount}
            loi={loi['#/chinhSach/challengeCount']}
            suaDuoc={suaDuoc}
            onDoi={(n) => dat('challengeCount', n)}
          />
          <ONhapSo
            nhan={t('ekyc.chinhSachQt.soLanLiveness')}
            giaTri={cs.maxLivenessRetry}
            loi={loi['#/chinhSach/maxLivenessRetry']}
            suaDuoc={suaDuoc}
            onDoi={(n) => dat('maxLivenessRetry', n)}
          />
        </div>
        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium text-content-primary">{t('ekyc.chinhSachQt.boDongTac')}</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {luaChon.dongTac.map((v) => (
              <Checkbox
                key={v}
                checked={cs.challengeActions.includes(v)}
                disabled={!suaDuoc}
                onChange={(e) => dat('challengeActions', doiTap<DongTac>(cs.challengeActions, v, e.target.checked))}
              >
                {t(TEN_DONG_TAC[v])}
              </Checkbox>
            ))}
          </div>
          {loi['#/chinhSach/challengeActions'] && (
            <p role="alert" className="text-xs text-danger-700">{loi['#/chinhSach/challengeActions']}</p>
          )}
        </fieldset>
        <Checkbox
          checked={cs.challengeRandomOrder}
          disabled={!suaDuoc}
          onChange={(e) => dat('challengeRandomOrder', e.target.checked)}
        >
          {t('ekyc.chinhSachQt.thuTuNgauNhien')}
        </Checkbox>
        <ChonKhiThieu
          nhan={t('ekyc.chinhSachQt.khiHetLuotLiveness')}
          giaTri={cs.onLivenessRetryExceeded}
          ds={luaChon.khiThieu}
          suaDuoc={suaDuoc}
          loi={loi['#/chinhSach/onLivenessRetryExceeded']}
          onDoi={(v) => dat('onLivenessRetryExceeded', v)}
        />
      </Nhom>

      <Nhom tieuDe={t('ekyc.chinhSachQt.giayToTieuDe')}>
        <div className="grid items-start gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <Field label={t('ekyc.chinhSachQt.nguonOcr')} hint={t('ekyc.chinhSachQt.nguonOcrGoiY')} error={loi['#/chinhSach/ocrSource']}>
            {(p) => (
              <Select
                className="w-full"
                id={p.id}
                aria-describedby={p['aria-describedby']}
                value={cs.ocrSource}
                disabled={!suaDuoc}
                onChange={(v) => dat('ocrSource', v)}
                options={luaChon.nguonOcr.map((v) => ({ value: v, label: t(TEN_NGUON_OCR[v]) }))}
              />
            )}
          </Field>
          <ONhapSo
            nhan={t('ekyc.chinhSachQt.soLanChupLai')}
            giaTri={cs.maxDocumentRetry}
            loi={loi['#/chinhSach/maxDocumentRetry']}
            suaDuoc={suaDuoc}
            onDoi={(n) => dat('maxDocumentRetry', n)}
          />
          <ONhapSo
            nhan={t('ekyc.chinhSachQt.tuoiToiThieu')}
            goiY={t('ekyc.chinhSachQt.tuoiToiThieuGoiY')}
            giaTri={cs.minAge}
            loi={loi['#/chinhSach/minAge']}
            suaDuoc={suaDuoc}
            onDoi={(n) => dat('minAge', Number.isNaN(n) ? null : n)}
          />
        </div>
        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium text-content-primary">{t('ekyc.chinhSachQt.truongTraVe')}</legend>
          <div className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2 xl:grid-cols-3">
            {luaChon.truongGiayTo.map((v) => (
              <Checkbox
                key={v}
                checked={cs.documentFields.includes(v)}
                disabled={!suaDuoc}
                onChange={(e) => dat('documentFields', doiTap(cs.documentFields, v, e.target.checked))}
              >
                {TEN_TRUONG_GIAY_TO[v] ? t(TEN_TRUONG_GIAY_TO[v]) : v}
              </Checkbox>
            ))}
          </div>
          {loi['#/chinhSach/documentFields'] && (
            <p role="alert" className="text-xs text-danger-700">{loi['#/chinhSach/documentFields']}</p>
          )}
        </fieldset>
        <ChonKhiThieu
          nhan={t('ekyc.chinhSachQt.khiThieuCoQuan')}
          giaTri={cs.onAuthorityUnavailable}
          ds={luaChon.khiThieu}
          suaDuoc={suaDuoc}
          loi={loi['#/chinhSach/onAuthorityUnavailable']}
          onDoi={(v) => dat('onAuthorityUnavailable', v)}
        />
      </Nhom>

      <Nhom tieuDe={t('ekyc.chinhSachQt.webhookTieuDe')}>
        <Checkbox checked={cs.sendWebhook} disabled={!suaDuoc} onChange={(e) => dat('sendWebhook', e.target.checked)}>
          {t('ekyc.chinhSachQt.guiWebhook')}
        </Checkbox>
        <p className="text-xs text-content-muted">{t('ekyc.chinhSachQt.guiWebhookMo')}</p>
      </Nhom>
    </div>
  );
}

/** Thiếu một phép kiểm bắt buộc thì chỉ có người duyệt hoặc từ chối — không có lựa chọn tự duyệt (ADR-07 của v3). */
function ChonKhiThieu({
  nhan,
  giaTri,
  ds,
  suaDuoc,
  loi,
  onDoi,
}: {
  nhan: string;
  giaTri: KhiThieu;
  ds: readonly KhiThieu[];
  suaDuoc: boolean;
  loi?: string;
  onDoi: (v: KhiThieu) => void;
}) {
  const { t } = useT();
  return (
    <Field label={nhan} error={loi} className="sm:max-w-sm">
      {(p) => (
        <Select
          className="w-full"
          id={p.id}
          aria-describedby={p['aria-describedby']}
          value={giaTri}
          disabled={!suaDuoc}
          onChange={onDoi}
          options={ds.map((v) => ({ value: v, label: t(TEN_KHI_THIEU[v]) }))}
        />
      )}
    </Field>
  );
}
